import { useState, useEffect, useRef, Children, cloneElement } from 'react';
import { CircleInfo } from './../../../assets/Icons';
import type { IChartBox } from './../../../types/charts/chart_box';
import { Tooltip } from '../../Tooltip';
import { getHorizontalPlacement, getVerticalPlacement } from './../../../utils/getPlacement';
import { detectScrollEnd, hasScrollBar } from './../../../utils/scrollUtils';
import { useScreenWidth } from './../../../hooks/useScreenWidth';
import styles from './chart-box.module.scss';

const MOBILE_BREAKPOINT = 900;

export const ChartBox = ({
  children,
  width = '100%',
  height,
  title,
  info,
  legendPlacement = 'bottom',
  legendList,
  addOverlayOnOverflow = false
}: IChartBox) => {

  const iconRef = useRef<any>();
  const chartBodyRef = useRef<HTMLDivElement>(null);

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const [placement, setPlacement] = useState<any>(`top ${getHorizontalPlacement(iconRef)}`);

  const checkIsScrollable = () => {
    const isScrollable = hasScrollBar(chartBodyRef);
    setHasScroll(isScrollable);
  };

  useEffect(() => {
    if (addOverlayOnOverflow) {
      checkIsScrollable();

      const handleScroll = () => {
        checkIsScrollable();

        const isScrolledToEnd = detectScrollEnd(chartBodyRef);
        setIsScrolledToBottom(isScrolledToEnd);
      };

      const container = chartBodyRef.current;
      container?.addEventListener("scroll", handleScroll);
      window?.addEventListener("resize", handleScroll);
      return () => {
        container?.removeEventListener("scroll", handleScroll)
        window?.addEventListener("resize", handleScroll);
      };
    }
  }, [addOverlayOnOverflow]);

  const colors = legendList?.map(item => item.color);
  const labels = legendList?.map(item => item.label);

  const calculateTooltipPlacement = (iconRef: any) => {
    setPlacement(
      isMobile ?
        `${getVerticalPlacement(iconRef)} center`
        :
        `top ${getHorizontalPlacement(iconRef)}`
    );
  };

  return (
    <div
      className={styles["chart-box"]}
      data-chart-scrolled-to-bottom={isScrolledToBottom}
      data-chart-has-scrollbar={hasScroll}
      data-chart-add-overlay={addOverlayOnOverflow}
      style={{ width, height }}
    >
      <div className={styles["chart-box__header"]}>
        {
          title &&
          <div className={styles["chart-box__header__title-box"]}>
            <div className={styles["chart-box__header__title-box__title"]}>
              {title}
            </div>

            <Tooltip
              hintText={info}
              triggerOn='hover'
              centerArrow
              placement={placement}
              handleMouseOver={() => calculateTooltipPlacement(iconRef)}
            >
              <div className={styles["chart-box__header__title-box__icon"]} ref={iconRef}>{CircleInfo}</div>
            </Tooltip>
          </div>
        }

        {
          legendList && legendPlacement === 'top' &&
          <div className={styles["chart-box__header__side-legend"]}>
            {
              legendList?.slice(0, 2).map((legendItem, i: number) =>
                <div className={styles["chart-box__header__side-legend__legend-item"]} key={`chart-side-legend-${i}`}>
                  <span
                    style={{ backgroundColor: legendItem.color }}
                    className={styles["chart-box__header__side-legend__legend-item__legend-key"]}></span>
                  <span className={styles["chart-box__header__side-legend__legend-item__label"]}>{legendItem.label}</span>
                </div>
              )
            }
          </div>
        }
      </div>

      {
        children &&
        <div
          className={styles["chart-box__body"]}
          ref={chartBodyRef}
        >
          {Children.map(children, (child: any) =>
            cloneElement(child, { colors, labels })
          )}
        </div>
      }

      {
        legendList && legendPlacement === 'bottom' && legendList?.length > 0 &&
        <div className={styles["chart-box__footer"]}>
          {
            legendList?.map((legendItem, i: number) =>
              <div className={styles["chart-box__footer__legend-item"]} key={`chart-footer-legend-${i}`}>
                <span
                  style={{ backgroundColor: legendItem.color }}
                  className={styles["chart-box__footer__legend-item__legend-key"]}></span>
                <span className={styles["chart-box__footer__legend-item__label"]}>{legendItem.label}</span>
              </div>
            )
          }
        </div>
      }
    </div>
  )
};